import React from "react";
import { motion } from "framer-motion";

/**
 * Technical Specification Table — dark sticky-header comparison table.
 * Column count is dynamic based on the `columns` prop.
 *
 * Works for both:
 *  - Multi-model comparison (e.g., LTC750 / LTC1000 / LTC1500 columns)
 *  - Simple 2-column parameter/value spec sheets (electrical, mast details, etc.)
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Data Sheet")
 *  - heading (ReactNode)
 *  - subtitle
 *  - columns: string[]     (column headers, e.g., ["LTC750", "LTC1000", "LTC1500"])
 *  - rows: { parameter: string, values: string[] }[]  (values.length must match columns.length; supports multi-line via \n)
 *  - footerNote?: string
 *  - variant?: "dark" | "light"  (default "dark")
 *  - minWidthClass?: string  (default "min-w-[640px]")
 *  - maxWidthClass?: string  (default "max-w-4xl")
 */
export default function TechnicalSpecificationTable({
  testId,
  eyebrow = "Data Sheet",
  heading,
  subtitle,
  columns = [],
  rows = [],
  footerNote = "* Specifications are subject to continuous product improvement without prior notice.",
  variant = "dark",
  minWidthClass = "min-w-[640px]",
  maxWidthClass = "max-w-4xl",
}) {
  const isDark = variant === "dark";

  return (
    <section
      data-testid={testId}
      className={`relative py-24 md:py-32 overflow-hidden ${
        isDark
          ? "bg-liftile-black"
          : "bg-liftile-cream-light text-liftile-matte-black"
      }`}
    >
      {isDark && (
        <div
          aria-hidden
          className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30"
        />
      )}

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="max-w-3xl mx-auto text-center mb-14 md:mb-16"
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2
            className={`display-2 ${isDark ? "text-liftile-cream" : ""}`}
          >
            {heading}
          </h2>
          {subtitle && (
            <p
              className={`font-body text-base md:text-lg mt-6 leading-relaxed max-w-2xl mx-auto ${
                isDark
                  ? "text-liftile-cream/70"
                  : "text-liftile-matte-black/70"
              }`}
            >
              {subtitle}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7 }}
          className={
            isDark
              ? `${maxWidthClass} mx-auto rounded-3xl overflow-hidden border border-white/[0.08] bg-white/[0.02] shadow-[0_25px_60px_-20px_rgba(0,0,0,0.5)]`
              : `${maxWidthClass} mx-auto rounded-3xl overflow-hidden border border-liftile-matte-black/[0.08] bg-white shadow-[0_20px_60px_-25px_rgba(0,0,0,0.15)]`
          }
        >
          <div className="overflow-x-auto">
            <table
              data-testid={`${testId}-table`}
              className={`w-full ${minWidthClass} border-collapse`}
            >
              <thead className="sticky top-0 z-10">
                {isDark ? (
                  <tr className="bg-liftile-orange/95 backdrop-blur">
                    <th className="text-left font-heading text-[11px] md:text-xs uppercase tracking-[0.18em] text-white font-semibold px-5 md:px-7 py-4">
                      Parameter
                    </th>
                    {columns.map((c) => (
                      <th
                        key={c}
                        className="text-left font-heading text-[11px] md:text-xs uppercase tracking-[0.18em] text-white font-semibold px-5 md:px-7 py-4 border-l border-white/25"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                ) : (
                  <tr className="bg-liftile-matte-black">
                    <th className="text-left font-heading text-[11px] md:text-xs uppercase tracking-[0.18em] text-liftile-cream/85 font-semibold px-5 md:px-7 py-4 w-[38%]">
                      Parameter
                    </th>
                    {columns.map((c) => (
                      <th
                        key={c}
                        className="text-left font-heading text-[11px] md:text-xs uppercase tracking-[0.18em] text-white font-semibold px-5 md:px-7 py-4 border-l border-white/15"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr
                    key={row.parameter}
                    className={
                      isDark
                        ? `border-b border-white/[0.06] transition-colors hover:bg-liftile-orange/[0.05] ${
                            ri % 2 === 0 ? "bg-white/[0.015]" : "bg-transparent"
                          }`
                        : `border-b border-liftile-matte-black/[0.06] transition-colors hover:bg-liftile-orange/[0.05] ${
                            ri % 2 === 0
                              ? "bg-liftile-cream-light/50"
                              : "bg-transparent"
                          }`
                    }
                  >
                    <td className="font-heading text-[11px] md:text-[12px] uppercase tracking-[0.15em] text-liftile-orange font-semibold px-5 md:px-7 py-4 md:py-5 align-top">
                      {row.parameter}
                    </td>
                    {row.values.map((v, vi) => (
                      <td
                        key={vi}
                        className={
                          isDark
                            ? "font-body text-sm md:text-[15px] text-liftile-cream/90 px-5 md:px-7 py-4 md:py-5 align-top border-l border-white/[0.06] whitespace-pre-line"
                            : "font-body text-sm md:text-[15px] text-liftile-matte-black/85 px-5 md:px-7 py-4 md:py-5 align-top border-l border-liftile-matte-black/[0.06] whitespace-pre-line"
                        }
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {footerNote && (
          <p
            className={`font-body italic text-xs md:text-sm text-center mt-6 max-w-2xl mx-auto ${
              isDark
                ? "text-liftile-cream/50"
                : "text-liftile-matte-black/55"
            }`}
          >
            {footerNote}
          </p>
        )}
      </div>
    </section>
  );
}
